"use client";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Avatar, Button, TextField, IconButton, Tooltip, Snackbar, Alert } from "@mui/material";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import { saveUserAvatar } from "@/lib/api";

export default function ProfilePanel() {
  const router = useRouter();
  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
  const fileRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    try {
      setName(localStorage.getItem("user_name") || "");
      setEmail(localStorage.getItem("user_email") || "");
      setAvatarUrl(localStorage.getItem("user_avatar_url"));
    } catch {}
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("user_email");
      localStorage.removeItem("user_name");
      localStorage.removeItem("user_id");
    } catch {}
    router.push("/login");
  };

  async function uploadAvatar(file: File) {
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      const res = await fetch("/api/files/upload", { method: "POST", body: fd });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "upload failed");
      }
      const data = await res.json();
      const url = data?.url as string | undefined;
      if (url) {
        localStorage.setItem("user_avatar_url", url);
        setAvatarUrl(url);
        try { window.dispatchEvent(new Event("storage")); } catch {}
        // Persist to backend DB (avatar column) if supported
        try {
          await saveUserAvatar(url);
          setUploadStatus({ type: 'success', message: 'Profile picture updated successfully!' });
        } catch (err) {
          console.warn("Failed to save avatar to backend", err);
          setUploadStatus({ type: 'success', message: 'Profile picture updated locally!' });
        }
      }
    } catch (err: any) {
      console.warn("avatar upload failed", err);
      setUploadStatus({ type: 'error', message: err?.message || 'Failed to upload profile picture' });
    }
  }

  const onPickFile = () => { fileRef.current?.click(); };

  return (
    <div className="h-full w-full p-4 text-gray-100">
      <div className="flex flex-col items-center gap-3 pt-8">
        <div className="relative">
          <Avatar src={avatarUrl || undefined} sx={{ width: 96, height: 96, bgcolor: "#4F46E5", fontSize: 36 }}>
            {(name?.[0] || "U").toUpperCase()}
          </Avatar>
          <Tooltip title={'Change photo'}>
            <IconButton
              size="small"
              onClick={onPickFile}
              sx={{ position: 'absolute', right: -6, bottom: -6, bgcolor: '#4F46E5', color: '#ffffff', border: '2px solid #e5e7eb', boxShadow: 2, '&:hover': { bgcolor: '#4338CA' } }}
            >
              <PhotoCamera fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadAvatar(f);
            e.currentTarget.value = "";
          }}
        />
        <div className="text-lg font-semibold">{name || "User"}</div>
        {/* <div className="text-sm text-gray-400">{email || ""}</div> */}
        <div className="mt-6 flex flex-col gap-3 w-full max-w-xs">
          <TextField
            label="Full name"
            size="small"
            value={name}
            disabled
            sx={{
              '& .MuiInputBase-input': { color: '#e5e7eb' },
              '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B7280' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1' },
              '& .MuiInputBase-root.Mui-disabled .MuiInputBase-input': { WebkitTextFillColor: '#9ca3af' },
            }}
          />
          <TextField
            label="Email"
            size="small"
            value={email}
            disabled
            sx={{
              '& .MuiInputBase-input': { color: '#e5e7eb' },
              '& .MuiInputLabel-root': { color: '#9ca3af' },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#374151' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#6B7280' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1' },
              '& .MuiInputBase-root.Mui-disabled .MuiInputBase-input': { WebkitTextFillColor: '#9ca3af' },
            }}
          />
          <div className="flex gap-2 justify-center mt-4">
            <Button variant="outlined" color="error" onClick={handleLogout}>Logout</Button>
          </div>
        </div>
      </div>
      {uploadStatus.type && (
        <Snackbar
          open={true}
          autoHideDuration={3000}
          onClose={() => setUploadStatus({ type: null, message: '' })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setUploadStatus({ type: null, message: '' })}
            severity={uploadStatus.type}
            sx={{ width: '100%' }}
          >
            {uploadStatus.message}
          </Alert>
        </Snackbar>
      )}
    </div>
  );
}
